import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Combobox,
  Group,
  InputBase,
  Loader,
  Modal,
  MultiSelect,
  Stack,
  Text,
  useCombobox,
} from '@mantine/core';
import { useDebounceValue } from 'usehooks-ts';
import { useGrantChurchAdminAccessMutation } from '@/hooks';
import { usePaginatedChurchOptions } from '@/hooks/churches';
import usersService from '@/services/users.service';
import {
  CHURCH_ADMIN_ACCESS_PRODUCT,
  CHURCH_ADMIN_ACCESS_PRODUCT_LABELS,
  type ChurchAdminAccessItem,
  type ChurchAdminSearchItem,
} from '@/types';
import styles from './ChurchAdminAccessModal.module.sass';

type SelectedChurchAdminUser = {
  userId: string;
  ssoUserId: string | null;
  email: string;
  firstName: string;
  lastName: string;
};

type ChurchAdminAccessModalProps = {
  opened: boolean;
  selectedAdmin: ChurchAdminAccessItem | null;
  onClose: () => void;
};

const PRODUCT_OPTIONS = Object.values(CHURCH_ADMIN_ACCESS_PRODUCT).map((product) => ({
  value: product,
  label: CHURCH_ADMIN_ACCESS_PRODUCT_LABELS[product],
}));

const getAdminOptionValue = (option: ChurchAdminSearchItem) =>
  `${option.userId ?? 'no-user-id'}:${option.ssoUserId ?? 'no-sso-id'}:${option.email}`;

export const ChurchAdminAccessModal = ({ opened, selectedAdmin, onClose }: ChurchAdminAccessModalProps) => {
  const [adminSearch, setAdminSearch] = useState<string>('');
  const [debouncedAdminSearch] = useDebounceValue(adminSearch, 400);
  const [adminOptions, setAdminOptions] = useState<ChurchAdminSearchItem[]>([]);
  const [isSearchingAdmins, setIsSearchingAdmins] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<SelectedChurchAdminUser | null>(null);

  const [churchSearch, setChurchSearch] = useState<string>('');
  const [selectedChurchIds, setSelectedChurchIds] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<CHURCH_ADMIN_ACCESS_PRODUCT[]>([]);

  const { churchOptions, isSearchingChurches, isLoadingMoreChurches, scrollAreaProps } = usePaginatedChurchOptions({
    searchTerm: churchSearch,
    enabled: opened,
  });

  const adminCombobox = useCombobox({ onDropdownClose: () => adminCombobox.resetSelectedOption() });
  const grantChurchAccessMutation = useGrantChurchAdminAccessMutation();

  useEffect(() => {
    if (!opened) return;

    setSelectedProducts([]);
    setSelectedChurchIds([]);
    setChurchSearch('');
    setAdminOptions([]);

    if (selectedAdmin) {
      setSelectedUser({
        userId: selectedAdmin.userId,
        ssoUserId: selectedAdmin.ssoUserId,
        email: selectedAdmin.email,
        firstName: selectedAdmin.firstName,
        lastName: selectedAdmin.lastName,
      });
      const fullName = `${selectedAdmin.firstName} ${selectedAdmin.lastName}`.trim();
      setAdminSearch(fullName || selectedAdmin.email);
      return;
    }

    setSelectedUser(null);
    setAdminSearch('');
  }, [opened, selectedAdmin]);

  useEffect(() => {
    if (!opened || debouncedAdminSearch.length < 3) {
      setAdminOptions([]);
      return;
    }

    const searchAdmins = async () => {
      setIsSearchingAdmins(true);
      try {
        const results = await usersService.searchChurchAdmins(debouncedAdminSearch);
        setAdminOptions(results);
      } finally {
        setIsSearchingAdmins(false);
      }
    };

    searchAdmins();
  }, [debouncedAdminSearch, opened]);

  const currentUserChurchIds = useMemo(
    () => new Set(selectedAdmin?.churches.map((church) => church.id) ?? []),
    [selectedAdmin]
  );
  const filteredChurchOptions = useMemo(
    () => churchOptions.filter((option) => !currentUserChurchIds.has(option.value)),
    [churchOptions, currentUserChurchIds]
  );

  const canSubmit = useMemo(() => {
    return Boolean(selectedUser?.userId) && selectedChurchIds.length > 0 && selectedProducts.length > 0;
  }, [selectedChurchIds, selectedProducts, selectedUser?.userId]);

  const handleAdminSelect = (option: ChurchAdminSearchItem) => {
    setSelectedUser({
      userId: option.userId ?? '',
      ssoUserId: option.ssoUserId,
      email: option.email,
      firstName: option.firstName,
      lastName: option.lastName,
    });
    const fullName = `${option.firstName || ''} ${option.lastName || ''}`.trim();
    setAdminSearch(fullName || option.email);
    setAdminOptions([]);
    adminCombobox.closeDropdown();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !selectedUser) return;

    await Promise.all(
      selectedChurchIds.map((churchId) =>
        grantChurchAccessMutation.mutateAsync({
          churchId,
          userId: selectedUser.userId,
          payload: { products: selectedProducts },
        })
      )
    );

    onClose();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Assign Church Access" size="lg">
      <Stack gap="md">
        <Combobox
          store={adminCombobox}
          withinPortal={false}
          onOptionSubmit={(value) => {
            const selectedOption = adminOptions.find((item) => getAdminOptionValue(item) === value);
            if (selectedOption) {
              handleAdminSelect(selectedOption);
            }
          }}
        >
          <Combobox.Target>
            <InputBase
              component="input"
              label="Find Admin"
              placeholder="Search by name or email"
              value={adminSearch}
              onChange={(event) => {
                setAdminSearch(event.currentTarget.value);
                if (!event.currentTarget.value) {
                  setSelectedUser(null);
                }
                adminCombobox.openDropdown();
              }}
              rightSection={isSearchingAdmins ? <Loader size="xs" /> : undefined}
              required
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              {adminOptions.length === 0 ? (
                <Combobox.Empty>No matching users</Combobox.Empty>
              ) : (
                adminOptions.map((option) => {
                  const fullName = `${option.firstName || ''} ${option.lastName || ''}`.trim();
                  return (
                    <Combobox.Option key={getAdminOptionValue(option)} value={getAdminOptionValue(option)}>
                      <Stack gap={0}>
                        <Text>{fullName || option.email}</Text>
                        <Text size="xs" c="dimmed">
                          {option.email}
                        </Text>
                      </Stack>
                    </Combobox.Option>
                  );
                })
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <MultiSelect
          label="Assign Churches"
          placeholder="Search and select churches"
          data={filteredChurchOptions}
          searchable
          searchValue={churchSearch}
          onSearchChange={setChurchSearch}
          value={selectedChurchIds}
          onChange={setSelectedChurchIds}
          rightSection={isSearchingChurches || isLoadingMoreChurches ? <Loader size="xs" /> : undefined}
          scrollAreaProps={scrollAreaProps}
          nothingFoundMessage="No churches found"
          required
        />

        <MultiSelect
          label="Products"
          placeholder="Select products"
          data={PRODUCT_OPTIONS}
          value={selectedProducts}
          onChange={(values) => setSelectedProducts(values as CHURCH_ADMIN_ACCESS_PRODUCT[])}
          searchable={false}
          nothingFoundMessage="No products"
          required
        />

        <Group justify="flex-end" mt="sm" className={styles.actions}>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={grantChurchAccessMutation.isPending}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
